import { useState, useCallback } from 'react';
import { createPaymentIntent, getSavedPaymentMethods, detachPaymentMethod } from '../services/stripeService';
import type { StripePaymentIntentResponse, StripePaymentMethodDto } from '../services/api';
import { getApiErrorMessage } from '../types/api-errors';

export const useStripePayment = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initiatePayment = useCallback(async (
        amount: number,
        currency = 'usd',
        metadata?: Record<string, string>
    ): Promise<StripePaymentIntentResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await createPaymentIntent(amount, currency, metadata);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to initiate payment.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPaymentMethods = useCallback(async (): Promise<StripePaymentMethodDto[]> => {
        setLoading(true);
        setError(null);
        try {
            return await getSavedPaymentMethods();
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to load payment methods.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const removePaymentMethod = useCallback(async (paymentMethodId: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await detachPaymentMethod(paymentMethodId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to remove payment method.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, initiatePayment, loadPaymentMethods, removePaymentMethod };
};
